import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackTrendyolConversion } from '../../../hooks/useConversionTracking';

export default function Products() {
  const { t } = useTranslation();
  const [yogaTowelColor, setYogaTowelColor] = useState<'brown' | 'crimson'>('brown');
  const [completeSetColor, setCompleteSetColor] = useState<'brown' | 'crimson'>('brown');

  const yogaTowelVariants = {
    brown: {
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/3aa1557c98410560fed490de6ee8c7ac.png',
      colorLabel: t('products.earthBrown'),
      trendyolUrl: 'https://ty.gl/w08pqn6i31wko'
    },
    crimson: {
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/c3f06a2213fcd89ebefb9b733494c5c3.png',
      colorLabel: t('products.crimson'),
      trendyolUrl: 'https://ty.gl/zew6zwpkgbm6x'
    }
  };

  const completeSetVariants = {
    brown: {
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/c454954c91421b0b03c9dd881e186aa9.png',
      colorLabel: t('products.earthBrown'),
      trendyolUrl: 'https://ty.gl/2hqx5nglpzmch'
    },
    crimson: {
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/0c15438954f6c422bae0d5faa57555a2.png',
      colorLabel: t('products.crimson'),
      trendyolUrl: 'https://ty.gl/azytgipruspvv'
    }
  };

  const products = [
    {
      id: 4,
      name: t('products.completeSet'),
      description: t('products.completeSetDesc'),
      image: completeSetVariants[completeSetColor].image,
      color: completeSetVariants[completeSetColor].colorLabel,
      hasColorSwitch: 'completeSet' as const,
      trendyolUrl: completeSetVariants[completeSetColor].trendyolUrl
    },
    {
      id: 1,
      name: t('products.pilatesReformerBrown'),
      description: t('products.pilatesReformerBrownDesc'),
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/0d0652bb4a20a2d177b11f91b1aea8c9.png',
      color: t('products.brown'),
      trendyolUrl: 'https://ty.gl/1xexcnsg70rfw'
    },
    {
      id: 2,
      name: t('products.pilatesReformerCrimson'),
      description: t('products.pilatesReformerCrimsonDesc'),
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/9c36db90c15e1ae093b4c1e5ba8b183a.png',
      color: t('products.crimson'),
      flip: true,
      trendyolUrl: 'https://ty.gl/azytgipruspvv'
    },
    {
      id: 3,
      name: t('products.yogaTowel'),
      description: t('products.yogaTowelDesc'),
      image: yogaTowelVariants[yogaTowelColor].image,
      color: yogaTowelVariants[yogaTowelColor].colorLabel,
      hasColorSwitch: 'yogaTowel' as const,
      trendyolUrl: yogaTowelVariants[yogaTowelColor].trendyolUrl
    }
  ];

  return (
    <section id="mysa-products" className="py-24 bg-gradient-to-b from-white to-red-50/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('products.title')}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            {t('products.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="relative w-full h-80 bg-gray-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full object-cover object-center ${product.flip ? 'scale-x-[-1]' : ''}`}
                />
                {product.hasColorSwitch && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-3 py-2 shadow-lg">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (product.hasColorSwitch === 'yogaTowel') setYogaTowelColor('brown');
                        if (product.hasColorSwitch === 'completeSet') setCompleteSetColor('brown');
                      }}
                      className={`w-6 h-6 rounded-full border-2 transition-all cursor-pointer ${
                        (product.hasColorSwitch === 'yogaTowel' ? yogaTowelColor : completeSetColor) === 'brown'
                          ? 'border-gray-800 ring-2 ring-gray-800/20 scale-110'
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: '#8B5A2B' }}
                      title={t('products.earthBrown')}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (product.hasColorSwitch === 'yogaTowel') setYogaTowelColor('crimson');
                        if (product.hasColorSwitch === 'completeSet') setCompleteSetColor('crimson');
                      }}
                      className={`w-6 h-6 rounded-full border-2 transition-all cursor-pointer ${
                        (product.hasColorSwitch === 'yogaTowel' ? yogaTowelColor : completeSetColor) === 'crimson'
                          ? 'border-gray-800 ring-2 ring-gray-800/20 scale-110'
                          : 'border-transparent hover:border-gray-300'
                      }`}
                      style={{ backgroundColor: '#DC143C' }}
                      title={t('products.crimson')}
                    />
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-amber-800 bg-amber-50 px-3 py-1 rounded-full whitespace-nowrap">
                    {product.color}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                  {product.description}
                </p>
                <a
                  href={product.trendyolUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={trackTrendyolConversion}
                  className="inline-flex items-center gap-2 text-xs text-orange-600 hover:text-orange-700 transition-colors cursor-pointer font-medium"
                >
                  <img
                    src="https://public.readdy.ai/ai/img_res/3a83f308-cb9c-4f00-817a-c62ca7cd5bb5.png"
                    alt="Trendyol"
                    className="w-16 h-auto object-contain"
                  />
                  <span className="whitespace-nowrap">{t('pricing.buyOnTrendyol')}</span>
                  <i className="ri-external-link-line text-[10px]"></i>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
